import * as React from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { DayPicker, NavProps } from 'react-day-picker';
import { cn } from '@/utils/utils';
import { format } from 'date-fns';
import { buttonVariants } from '../common/Button';
import { useTranslation } from 'react-i18next';
import { getLocaleDateFns } from '@/utils/language';
import { enUS, ja } from 'date-fns/locale';
import { LANGUAGES_SUPPORTED } from '@/types/enums';

export type ReactDayPickerProps = React.ComponentProps<typeof DayPicker> & {
    disabledSelect?: boolean,
    disableHoverday?: boolean
};

function ReactDayPickerRange({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'dropdown',
    disabledSelect = false,
    disableHoverday = false,
    ...props
}: ReactDayPickerProps) {
    // hooks
    const { i18n: { language } } = useTranslation();
    const localeDateFns = getLocaleDateFns(language);

    const navigationButtonStyles = cn(
        buttonVariants({ variant: 'ghostOutlineGray' }),
        'h-7 w-7 rounded-full fill-[currentColor] p-0'
    );

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('xsm:!p-10 px-5 py-6 h-full rounded-md border border-border-gray2 shadow-[2px_2px_15px_rgba(0,0,0,0.05)] day-picker-in-range', className)}
            classNames={{
                nav: 'flex items-center justify-between',
                chevron: 'w-4 h-4',
                button_next: navigationButtonStyles,
                button_previous: navigationButtonStyles,
                caption_label: cn('text-fs-mid12 font-semibold leading-[18px]', captionLayout !== 'label' && 'hidden'),
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'size-[35px] rounded-full font-semibold text-center !text-fs-mid16 p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 hover:cursor-pointer',
                    disableHoverday && 'pointer-events-none'
                ),
                day_button: 'h-11 w-11',
                selected: cn(
                    !disabledSelect ?
                        '!bg-[#FFBF34] text-white hover:bg-[#FFBF34] hover:text-white focus:bg-[#FFBF34] focus:text-black' :
                        ''
                ),
                disabled: 'text-muted-foreground opacity-50',
                focused: '',
                hidden: 'invisible',
                outside: 'day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
                today: 'bg-none text-accent-foreground ',
                dropdown: 'border-none outline-none hover:cursor-pointer',
                dropdown_root: '',
                dropdowns: 'flex flex-row-reverse gap-2 w-[100px] items-center justify-center',
                footer: '',
                month: 'w-full border-collapse',
                month_caption: 'absolute top-2 left-1/2 -translate-x-1/2 flex items-center',
                month_grid: 'w-full',
                months: 'relative flex flex-col',
                months_dropdown: '',
                week: 'flex w-full mt-2 justify-between',
                week_number: '',
                week_number_header: '',
                weekday: 'text-border-input-2 rounded-md w-9 font-medium text-[10px]',
                weekdays: 'flex h-10 items-center justify-between mt-2.5',
                weeks: '',
                years_dropdown: '',
                ...classNames
            }}
            captionLayout={captionLayout}
            components={{
                Chevron: ({ className, orientation = 'down' }) => {
                    const iconChevron = {
                        'down': ChevronDown,
                        'up': ChevronUp,
                        'left': ChevronLeft,
                        'right': ChevronRight
                    };

                    const Comp = iconChevron[orientation];

                    return (
                        <Comp className={className} />
                    );
                },
                Nav: ({ children, className, ...props }: NavProps) => {
                    const [previousButton, nextButton] = React.Children.map(children, child => child) as Array<React.ReactElement<any, string | React.JSXElementConstructor<any>>>;

                    return (
                        <nav className={cn('flex items-center min-h-8 justify-between', className)} {...props}>
                            {previousButton}
                            {nextButton}
                        </nav>
                    );
                }
            }}
            locale={localeDateFns}
            formatters={{
                formatMonthDropdown: (month) => {
                    const formatMonth = 'MMM';
                    return format(new Date().setMonth(month), formatMonth);
                },
                formatCaption: (date) => {
                    const locale = language === LANGUAGES_SUPPORTED.Japanese ? ja : enUS;
                    return format(date, 'yyyy / MM', { locale });
                }
            }}
            endMonth={new Date((new Date()).getFullYear() + 30, 11, 31)}
            disabled={disabledSelect}
            {...props}
        />
    );
}
ReactDayPickerRange.displayName = 'ReactDayPickerRange';

export { ReactDayPickerRange };
